import React, { useRef, useState, useEffect } from 'react';
import './Sage.css';
import DecryptedText from '../blocks/DecryptedText/DecryptedText';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import { Language, detectBrowserLanguage, getTranslations } from '../utils/localization';

const Sage: React.FC = () => {
  // Language state
  const [language, setLanguage] = useState<Language>(detectBrowserLanguage());
  const translations = getTranslations(language);
  const sageTranslations = translations.sage;
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  const [showBackToTop, setShowBackToTop] = useState(false);

  // Back to top visibility logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      <div className="project-page sage-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div className="language-switcher">
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1>
              <DecryptedText 
                text={sageTranslations.title}
                animateOn="view"
                sequential={true}
                maxIterations={4}
                speed={60}
                className="decrypted-title"
                parentClassName="title-container"
                encryptedClassName="encrypted-title"
              />
            </h1>
            <hr />
            <h2>
              <DecryptedText 
                text={sageTranslations.subtitle}
                animateOn="view"
                sequential={true}
                maxIterations={1}
                speed={20}
                className="decrypted-description"
                parentClassName="description-container"
                encryptedClassName="encrypted-description"
              />
            </h2>
          </div>
        </header>

        <main className="project-content">
          <div className="container">
            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{sageTranslations.navItems[0]}</h3>
              <p>
                {sageTranslations.sections.overview}
              </p>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section iframe-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <div className="iframe-container">
                <iframe
                  src="https://isannabeth.com/portfolio/sage/today"
                  title="Sage Application"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{sageTranslations.sections.features}</h3>
              <ul className="feature-list">
                <li>
                  <strong>{sageTranslations.features.personalizedLearning.title}:</strong> 
                  {sageTranslations.features.personalizedLearning.description}
                </li>
                <li>
                  <strong>{sageTranslations.features.dailyKnowledge.title}:</strong> 
                  {sageTranslations.features.dailyKnowledge.description}
                </li>
                <li>
                  <strong>{sageTranslations.features.communityContribution.title}:</strong> 
                  {sageTranslations.features.communityContribution.description}
                </li>
                <li>
                  <strong>{sageTranslations.features.personalArchive.title}:</strong> 
                  {sageTranslations.features.personalArchive.description}
                </li>
              </ul>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <h3>{sageTranslations.sections.technology}</h3>
              <ul className="tech-list">
                <li>
                  <strong>Node.js</strong> - {language === 'zh' ? "服务器端运行环境" : "Server-side runtime environment"}
                </li>
                <li>
                  <strong>Express</strong> - {language === 'zh' ? "web应用框架" : "Web application framework"}
                </li>
                <li>
                  <strong>MongoDB</strong> - {language === 'zh' ? "数据存储" : "Data storage"}
                </li>
                <li>
                  <strong>Passport.js</strong> - {language === 'zh' ? "用户认证" : "User authentication"}
                </li>
                <li>
                  <strong>Sass</strong> - {language === 'zh' ? "CSS预处理器" : "CSS preprocessor"}
                </li>
                <li>
                  <strong>Bootstrap</strong> - {language === 'zh' ? "CSS框架" : "CSS framework"}
                </li>
                <li>
                  <strong>Lunr.js</strong> - {language === 'zh' ? "全文搜索功能" : "Full-text search functionality"}
                </li>
              </ul>
            </SpotlightCard>
          </div>
        </main>

        {/* Back to Top Button */}
        <div 
          className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </div>
      </div>
    </ClickSpark>
  );
};

export default Sage; 